import { CategoryScale, Chart as ChartJS, ChartData, Legend, LineElement, LinearScale, PointElement, Title, Tooltip, TooltipModel } from 'chart.js'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { HelpOutline } from '@mui/icons-material'
import { Box, Button, Card, CardContent, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import { BlueprintFamilyTrendsPoint } from '../../../../api/core'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { colors } from '../../../../themes/theme'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { useSeriesHighlight } from '../../hooks/useSeriesHighlight'
import { TrendsChartLegend, TrendsChartLegendItem } from '../TrendsChartLegend'
import { CatalogueMetricKey } from '../helpers/catalogueUtils'
import { metricLensDescription, metricLensLabel } from '../helpers/metricLensCopy'
import { MonthlyBucket, keepsPartialTailForMetric, monthlyModeForMetric, toMonthlySeries } from '../helpers/trendsBucketing'
import { buildTrendsTooltipCallbacks, formatTrendsMonthLabel, formatTrendsTick, formatTrendsValue } from '../helpers/trendsChartFormat'
import { TrendsSelectedPeriod, selectedPeriodAxis } from '../helpers/trendsSelectedPeriod'
import { trendsGranularityOf } from '../helpers/trendsSeries'
import './TrendsLineChartCard.scss'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

export interface TrendsChartSeriesItem {
  id: string
  name: string
  // Localized, unsanitized glossary copy for the legend's hover tooltip.
  description?: string
  // The description is plain text (event types), not HTML (blueprint families).
  isPlainTextDescription?: boolean
}

export interface TrendsLineChartCardProps {
  title: string
  ariaLabel: string
  // Header of the first column in the screen-reader data table ("Blueprint
  // Family", "Event group", ...).
  nameColumnHeader: string
  items: TrendsChartSeriesItem[]
  // Series per item id, already narrowed to the active metric lens. Points may be
  // daily or monthly; the card detects which and only buckets the daily ones.
  seriesById: Record<string, BlueprintFamilyTrendsPoint[]>
  metric: CatalogueMetricKey
  isLoading?: boolean
  // The series request failed. Shown instead of the empty states, so a backend
  // failure never reads as "no data"; `isLoading` still wins while a retry runs.
  isError?: boolean
  // Offered as a retry button in the error state when given.
  onRetry?: () => void
  // Right-aligned caption next to the header controls ("Top 10 ... in view").
  subtitle?: string
  // Rendered between the header and the canvas.
  notice?: React.ReactNode
  // Right-aligned header slot for the control that changes the plotted series.
  headerControls?: React.ReactNode
  // Rendered under the canvas, full card width (e.g. the tracked-set strip).
  footer?: React.ReactNode
  // Shown when `items` is empty.
  emptyMessage?: string
  // Full-width row above the title, separated by a divider: the metric lens
  // tabs go here so they read as "which chart" and not as one more filter.
  tabs?: React.ReactNode
  // Render without the outlined Card shell, for embedding inside another card
  // (the detail pages put the chart under the stat tiles in one card).
  embedded?: boolean
  // The selected period: only its calendar months are plotted, whatever span the
  // series covers. Absent, every month the series carries is plotted.
  selectedPeriod?: TrendsSelectedPeriod
  // Line color per item id, so a caller can keep an entity's color across re-sorts.
  // Items without one take the palette color of their position.
  colorById?: Record<string, string>
}

// Series colors come from the shared chart palette, never from a domain color
// (event-category tag colors etc.), so ten simultaneous lines stay tellable apart.
// A caller that wants colors to outlive re-sorting passes `colorById`; otherwise a
// line's color is its position. Identity lives in the legend text.
const LINE_PALETTE = colors.chartSeries

/**
 * The one line chart of Live Event Analytics. Data fetching lives in thin
 * wrappers (`BlueprintTrendsChart`, `EventGroupTrendsChart`, ...) so every
 * surface plots, buckets, labels and explains its series identically:
 *
 * - daily points are reduced to one per completed calendar month (the month in
 *   progress is dropped, see `trendsBucketing`), labelled "Aug 2026";
 * - the metric definition and the period caveats live behind the help icon next
 *   to the title, and in the screen-reader text below it, never in the Chart.js
 *   hover (which cannot wrap and overflowed the canvas);
 * - the legend isolates series on click and explains each on hover.
 */
export const TrendsLineChartCard = React.memo(function TrendsLineChartCard({
  title,
  ariaLabel,
  nameColumnHeader,
  items,
  seriesById,
  metric,
  isLoading = false,
  isError = false,
  onRetry,
  subtitle,
  notice,
  headerControls,
  footer,
  emptyMessage,
  tabs,
  embedded = false,
  selectedPeriod,
  colorById,
}: TrendsLineChartCardProps) {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()

  const itemIds = useMemo(() => items.map((item) => item.id), [items])

  // Monthly points per series. Series that already arrive monthly pass through; a daily
  // series is bucketed, where `mode` follows the metric: adoption is an "as of the date"
  // figure (month-end snapshot), density and concurrent instances are per-day figures
  // (monthly mean, as the monthly endpoints build them). The in-progress month is
  // dropped for every lens: a few days of data would read as a sudden drop.
  const monthlyById = useMemo(() => {
    const mode = monthlyModeForMetric(metric)
    const keepPartialTail = keepsPartialTailForMetric(metric)
    const result: Record<string, MonthlyBucket[]> = {}
    items.forEach((item) => {
      const series = seriesById[item.id]
      result[item.id] = toMonthlySeries(series, trendsGranularityOf(series), { mode, keepPartialTail })
    })
    return result
  }, [items, seriesById, metric])

  const formatMonth = useCallback((month: string) => formatTrendsMonthLabel(month, userLanguage), [userLanguage])
  const formatValue = useCallback((value: number) => formatTrendsValue(metric, value), [metric])

  // Legend-driven isolation. Colors are resolved once here (`colorById`, else
  // position), so the swatches keep matching their lines while the plotted set narrows.
  const { highlightedIds, toggle, clear, isPlotted } = useSeriesHighlight(itemIds)

  const legendItems = useMemo<TrendsChartLegendItem[]>(
    () =>
      items.map((item, idx) => ({
        id: item.id,
        label: item.name,
        color: colorById?.[item.id] ?? LINE_PALETTE[idx % LINE_PALETTE.length],
        description: item.description,
        isPlainTextDescription: item.isPlainTextDescription,
      })),
    [items, colorById]
  )

  const { labels, datasets, hasAnyPoint, monthFacts } = useMemo(() => {
    // Unify the x-axis across all series (each may cover different months).
    // Isolated-away series still contribute months, so the axis doesn't shift
    // under the lines that stay.
    const monthSet = new Set<string>()
    items.forEach((item) => (monthlyById[item.id] ?? []).forEach((p) => monthSet.add(p.month)))
    const allMonths = Array.from(monthSet).sort()

    // With a selected period, only its calendar months are plotted (see
    // `selectedPeriodAxis`); anything else in the response stays off the axis.
    const sortedMonths = selectedPeriod ? selectedPeriodAxis(allMonths, selectedPeriod) : allMonths

    const plottedItems = legendItems.filter((legendItem) => isPlotted(legendItem.id))

    // What the hover can say about each month beyond the plotted value. Partial months
    // are an axis-wide fact (every series is clamped to the same window), so any series
    // flagging one is enough; a selected-period axis keeps whole months only, so it has none.
    // The counts belong to one entity, so they are only carried when a single line is
    // plotted — which is also the only case the tooltip shows them.
    const soleSeries = plottedItems.length === 1 ? monthlyById[plottedItems[0].id] ?? [] : []
    const countsByMonth = new Map(soleSeries.map((bucket) => [bucket.month, bucket]))
    const facts = sortedMonths.map((month) => ({
      partial: !selectedPeriod && items.some((item) => (monthlyById[item.id] ?? []).some((bucket) => bucket.month === month && bucket.partial)),
      distinctGamesCount: countsByMonth.get(month)?.distinctGamesCount,
      trackedGamesCount: countsByMonth.get(month)?.trackedGamesCount,
    }))

    const builtDatasets = plottedItems.map((legendItem) => {
      const byMonth = new Map((monthlyById[legendItem.id] ?? []).map((p) => [p.month, p.value]))
      return {
        label: legendItem.label,
        data: sortedMonths.map((m) => (byMonth.has(m) ? (byMonth.get(m) as number) : null)),
        borderColor: legendItem.color,
        backgroundColor: legendItem.color,
        spanGaps: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      }
    })

    return {
      labels: sortedMonths.map(formatMonth),
      datasets: builtDatasets,
      // Measured across every series, not just the plotted ones, so isolating a
      // series that happens to be empty doesn't read as "no data for range".
      hasAnyPoint: items.some((item) => (monthlyById[item.id] ?? []).length > 0),
      monthFacts: facts,
    }
  }, [items, legendItems, isPlotted, monthlyById, formatMonth, selectedPeriod])

  // Stable identity for Chart.js: an inline `{ labels, datasets }` would look
  // like new data on every parent render and force a canvas redraw.
  const chartData = useMemo<ChartData<'line'>>(() => ({ labels, datasets }), [labels, datasets])

  // The canvas tooltip draws one line per series left-to-right with no columns, so a
  // name and its value can only ever be as far apart as the name is long — there is no
  // way to right-align the numbers within it. `enabled: false` + `external` keeps
  // Chart.js computing everything (position, title, per-series colors, the
  // partial-month / month-games captions below), but hands the actual box to this
  // plain DOM element instead, which is free to lay out name/value as two flex columns.
  const chartAreaRef = useRef<HTMLDivElement>(null)
  const tooltipElRef = useRef<HTMLDivElement | null>(null)

  useEffect(
    () => () => {
      tooltipElRef.current?.remove()
      tooltipElRef.current = null
    },
    []
  )

  const externalTooltipHandler = useCallback(
    (context: { chart: ChartJS; tooltip: TooltipModel<'line'> }) => {
      const container = chartAreaRef.current
      if (!container) return
      const { tooltip } = context

      if (!tooltipElRef.current) {
        const el = document.createElement('div')
        el.className = 'TrendsChartTooltip'
        container.appendChild(el)
        tooltipElRef.current = el
      }
      const el = tooltipElRef.current

      if (tooltip.opacity === 0) {
        el.style.opacity = '0'
        return
      }

      el.replaceChildren()

      const titleEl = document.createElement('div')
      titleEl.className = 'TrendsChartTooltip__title'
      // Already carries the "· partial month" suffix — computed by the tested
      // `title` callback below, not reimplemented here.
      titleEl.textContent = tooltip.title?.[0] ?? ''
      el.appendChild(titleEl)

      tooltip.dataPoints?.forEach((dp, index) => {
        const row = document.createElement('div')
        row.className = 'TrendsChartTooltip__row'

        const dot = document.createElement('span')
        dot.className = 'TrendsChartTooltip__dot'
        dot.style.backgroundColor = String(tooltip.labelColors[index]?.backgroundColor ?? '')
        row.appendChild(dot)

        // `textContent`, not the label callback's formatted "Name: Value" string:
        // that string is exactly what made the columns impossible to line up, so the
        // two pieces are read straight off the data point and placed in their own cells.
        const name = document.createElement('span')
        name.className = 'TrendsChartTooltip__name'
        name.textContent = String(dp.dataset.label ?? '')
        row.appendChild(name)

        const raw = typeof dp.raw === 'number' ? dp.raw : Number(dp.raw)
        const value = document.createElement('span')
        value.className = 'TrendsChartTooltip__value'
        value.textContent = formatTrendsValue(metric, raw)
        row.appendChild(value)

        el.appendChild(row)

        // The single-series month-games caption, computed by the tested `afterLabel`
        // callback below and surfaced on `body[index].after`.
        const after = tooltip.body[index]?.after?.[0]
        if (after) {
          const caption = document.createElement('div')
          caption.className = 'TrendsChartTooltip__caption'
          caption.textContent = after
          el.appendChild(caption)
        }
      })

      // Chart.js already keeps its own tooltip inside the canvas by choosing which
      // edge of the box sits against the caret; mirror that choice with a matching
      // CSS transform rather than always centering above the point, which would
      // reintroduce the clipping Chart.js already solved.
      const translateX = tooltip.xAlign === 'left' ? '0' : tooltip.xAlign === 'right' ? '-100%' : '-50%'
      const translateY = tooltip.yAlign === 'top' ? '0' : tooltip.yAlign === 'bottom' ? '-100%' : '-50%'

      el.style.opacity = '1'
      el.style.left = `${tooltip.caretX}px`
      el.style.top = `${tooltip.caretY}px`
      el.style.transform = `translate(${translateX}, ${translateY})`
    },
    [metric]
  )

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        // Replaced by TrendsChartLegend: the built-in one hides on click and
        // wraps unpredictably with ten long names.
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: externalTooltipHandler,
          callbacks: buildTrendsTooltipCallbacks({
            metric,
            monthAt: (dataIndex: number) => monthFacts[dataIndex],
            labels: {
              partial: t('market-analytics:trends_tooltip_partial_month'),
              // Interpolated as `games`, not `count`: `count` would put i18next into
              // plural resolution and look for keys these strings do not have.
              monthGames: ({ count, tracked }) => t('market-analytics:trends_tooltip_month_games', { games: count, tracked }),
            },
          }),
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: number | string) => formatTrendsTick(metric, value),
            // Adoption ticks read as whole percents; without a 0.01 floor on the step a
            // small range (a few percent) yields repeated labels such as "0%, 1%, 1%, 2%".
            ...(metric === 'adoption' ? { precision: 2 } : {}),
          },
        },
        x: { grid: { display: false } },
      },
    }),
    [metric, monthFacts, t, externalTooltipHandler]
  )

  // Latest value per series for the screen-reader data table (charts render to an
  // opaque <canvas>, so a text equivalent is required for WCAG 1.1.1).
  const seriesSummary = useMemo(
    () =>
      items.map((item) => {
        const points = monthlyById[item.id] ?? []
        const latest = points.length > 0 ? points[points.length - 1].value : null
        return { id: item.id, name: item.name, latest }
      }),
    [items, monthlyById]
  )

  const definition = metricLensDescription(metric, t)
  const valueHeader = metricLensLabel(metric, t)

  // What the chart means, in reading order: the metric definition, then the caveat
  // this particular chart adds. One array so the help tooltip and the screen-reader
  // text below the title can never state different things.
  const descriptionLines = useMemo(() => {
    const lines = [definition]
    // Each adoption point is that month's own share of the cohort.
    if (selectedPeriod && metric === 'adoption') {
      lines.push(t('market-analytics:trends_adoption_last_point_note'))
    }
    return lines
  }, [definition, selectedPeriod, metric, t])

  // Hoisted so GRTooltip receives a stable element tree instead of a fresh one on
  // every render.
  const helpContent = useMemo(
    () => (
      <Stack spacing={0.75} sx={{ py: 0.5 }}>
        {descriptionLines.map((line) => (
          <Typography key={line} variant="caption">
            {line}
          </Typography>
        ))}
      </Stack>
    ),
    [descriptionLines]
  )

  // The canvas only renders when there is something to draw. No legend or footer
  // next to a skeleton, an error or an empty-state message: the legend would offer
  // isolation over a canvas that isn't drawing anything.
  const showChart = !isLoading && !isError && items.length > 0 && hasAnyPoint

  // The external tooltip lives outside the <Line>, so Chart.js can't hide it when
  // the canvas unmounts; without this the last tooltip stays painted over the
  // skeleton or message that replaced the chart.
  useEffect(() => {
    if (!showChart && tooltipElRef.current) tooltipElRef.current.style.opacity = '0'
  }, [showChart])

  const renderMessage = (message: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  )

  const body = (
    <>
      {tabs ? (
        // Negative margin cancels the host CardContent's horizontal padding
        // (this card's own when standalone, `LevelStatsCard`'s when embedded)
        // so the rule reaches the card's edges rather than the tiles' inset.
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mx: -2, px: 2, mt: embedded ? 0 : -1 }}>{tabs}</Box>
      ) : null}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: notice ? 0.75 : 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="h2" variant="h3">
            {title}
          </Typography>
          {/* Never squeezed out by a long title: the icon is the only way to the definition. */}
          <GRTooltip content={helpContent} styles={{ display: 'inline-flex', flexShrink: 0, cursor: 'help' }}>
            <IconButton
              size="small"
              aria-label={t('market-analytics:trends_chart_help_aria')}
              sx={{ color: 'text.disabled', cursor: 'help', '&:hover': { color: 'primary.main' } }}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
          </GRTooltip>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {headerControls}
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      {notice}
      {/* Text equivalent of the canvas chart for assistive tech: what the number
          means and how it is plotted, then the latest value per series. The
          same lines are in the help tooltip, but a hover is unreachable by
          keyboard and screen-reader users, so they are restated here. */}
      <Box sx={visuallyHidden}>
        {descriptionLines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        {seriesSummary.length > 0 ? (
          <table>
            <caption>{title}</caption>
            <thead>
              <tr>
                <th>{nameColumnHeader}</th>
                <th>{valueHeader}</th>
              </tr>
            </thead>
            <tbody>
              {seriesSummary.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.latest === null ? '—' : formatValue(s.latest)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box ref={chartAreaRef} sx={{ position: 'relative', height: 360 }}>
            {isLoading ? (
              <Skeleton variant="rounded" height="100%" width="100%" />
            ) : isError ? (
              <Stack role="alert" alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('market-analytics:trends_load_error')}
                </Typography>
                {onRetry ? (
                  <Button variant="outlined" size="small" onClick={onRetry}>
                    {t('market-analytics:retry')}
                  </Button>
                ) : null}
              </Stack>
            ) : items.length === 0 ? (
              renderMessage(emptyMessage ?? t('market-analytics:ranked_list_no_results'))
            ) : !hasAnyPoint ? (
              renderMessage(t('market-analytics:no_data_for_range'))
            ) : (
              // `role="img"` on the canvas only: children of an img are presentational,
              // so the skeleton and messages above would be hidden from assistive tech.
              <Box role="img" aria-label={ariaLabel} sx={{ height: '100%' }}>
                <Line data={chartData} options={chartOptions} />
              </Box>
            )}
          </Box>
          {showChart ? footer : null}
        </Box>
        {showChart ? <TrendsChartLegend items={legendItems} highlightedIds={highlightedIds} onToggle={toggle} onClearHighlight={clear} /> : null}
      </Stack>
    </>
  )

  if (embedded) return <Box>{body}</Box>

  return (
    <Card variant="outlined">
      <CardContent>{body}</CardContent>
    </Card>
  )
})

TrendsLineChartCard.displayName = 'TrendsLineChartCard'
